import React, { useEffect } from "react";
import { NavLink } from "react-router-dom";
import "../styles/Prime360.css";

export default function Prime360() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const strategicValues = [
    {
      number: "01",
      title: "Accountability",
      description:
        "Clarifies board, executive, operational, and functional accountability for safety governance and performance.",
    },
    {
      number: "02",
      title: "Integrated Health and Safety",
      description:
        "Connects occupational and psychological health and safety rather than treating them as separate systems.",
    },
    {
      number: "03",
      title: "Enterprise Alignment",
      description:
        "Links safety strategy with enterprise risk, resilience, ESG, IT, cybersecurity, business continuity, and organizational change.",
    },
    {
      number: "04",
      title: "Evidence-Oriented Improvement",
      description:
        "Provides a structured basis for gap analysis, implementation priorities, monitoring, and continual improvement.",
    },
    {
      number: "05",
      title: "Common Organizational Structure",
      description:
        "Creates a common structure for leadership, workers, operations, HR, technology, and safety functions.",
    },
    {
      number: "06",
      title: "System Modernization",
      description:
        "Supports the modernization of existing systems without replacing legal requirements, regulators, or recognized standards.",
    },
  ];

  const connectedElements = [
    {
      number: "01",
      title: "Organizational Structure & Integrity",
      description:
        "Governance, executive leadership, ethics, role clarity, and accountability.",
      areas: [
        "Governance structure",
        "Executive leadership",
        "Ethics and integrity",
        "Role clarity",
      ],
    },
    {
      number: "02",
      title: "Strategy & Design",
      description:
        "System scope, legal alignment, objectives, psychological health strategy, and business-system interdependencies.",
      areas: [
        "System scope",
        "Legal alignment",
        "Strategic objectives",
        "System interdependencies",
      ],
    },
    {
      number: "03",
      title: "Implementation & Execution",
      description:
        "Hazard controls, communication, engagement, competency, contractor safety, emergency readiness, and practical execution.",
      areas: [
        "Hazard controls",
        "Workforce engagement",
        "Competency",
        "Emergency readiness",
      ],
    },
    {
      number: "04",
      title: "Monitoring & Evaluation",
      description:
        "Performance information, investigations, culture assessment, auditing, and corrective action.",
      areas: [
        "Performance information",
        "Investigations",
        "Culture assessment",
        "Corrective action",
      ],
    },
    {
      number: "05",
      title: "Continuous Improvement & Adaptation",
      description:
        "Management review, change management, resilience, organizational learning, and system renewal.",
      areas: [
        "Management review",
        "Change management",
        "Resilience",
        "System renewal",
      ],
    },
  ];

  const primeLens = [
    {
      letter: "P",
      title: "Planning",
      description:
        "Define intent, priorities, responsibilities, and the pathway to implementation.",
    },
    {
      letter: "R",
      title: "Resilience",
      description:
        "Strengthen the organization’s ability to anticipate, respond, adapt, and recover.",
    },
    {
      letter: "I",
      title: "Integration",
      description:
        "Connect safety with governance, operations, people, technology, and enterprise systems.",
    },
    {
      letter: "M",
      title: "Measurement",
      description:
        "Use meaningful evidence to understand adoption, performance, and outcomes.",
    },
    {
      letter: "E",
      title: "Engagement",
      description:
        "Include leadership, workers, specialists, and operational stakeholders in the system.",
    },
  ];

  const standardsContext = [
    "ISO 45001",
    "CSA Z1003",
    "COR",
    "Vision Zero Principles",
    "Governance References",
    "Enterprise Risk References",
  ];

  return (
    <main className="prime360-page">
      {/* PAGE HERO */}
      <section
        className="prime360-hero"
        aria-labelledby="prime360-hero-title"
      >
        <div className="prime360-hero-background" aria-hidden="true">
          <span className="prime360-hero-glow prime360-glow-left"></span>
          <span className="prime360-hero-glow prime360-glow-right"></span>

          <span className="prime360-hero-orbit prime360-orbit-one"></span>
          <span className="prime360-hero-orbit prime360-orbit-two"></span>
          <span className="prime360-hero-orbit prime360-orbit-three"></span>
        </div>

        <div className="prime360-hero-container">
          {/* <div className="prime360-status">
            <span className="prime360-status-dot" aria-hidden="true"></span>
            <span>Current Status: In Validation</span>
          </div> */}

          <p className="prime360-kicker">
            Developing Governance Framework
          </p>

          <h1 id="prime360-hero-title" className="prime360-hero-title">
            Safety Governance Designed for
            <span> the Whole Organization.</span>
          </h1>

          <p className="prime360-hero-description">
            PRIME360™ is a developing governance-driven framework for
            integrating occupational and psychological health and safety with
            leadership, strategy, implementation, measurement, and continual
            improvement.
          </p>

          <div className="prime360-hero-buttons">
            <NavLink
              to="/contact"
              className="prime360-primary-button"
            >
              <span>Discuss PRIME360™</span>

              <span
                className="prime360-button-arrow"
                aria-hidden="true"
              >
                ↗
              </span>
            </NavLink>

            <NavLink
              to="/contact"
              className="prime360-secondary-button"
            >
              <span>Request an Executive Brief</span>
              <span aria-hidden="true">→</span>
            </NavLink>
          </div>

          <div className="prime360-validation-note">
            <span className="prime360-validation-icon" aria-hidden="true">
              i
            </span>

            <p>
              PRIME360™ is undergoing further evidence integration, standards
              alignment, and review. It remains a developing framework and is
              not currently presented as a published external standard or
              certification scheme.
            </p>
          </div>
        </div>
      </section>

      {/* WHY PRIME360 */}
      <section
        className="prime360-why"
        aria-labelledby="prime360-why-title"
      >
        <div className="prime360-why-container">
          <div className="prime360-why-heading">
            <p className="prime360-section-kicker">
              Why PRIME360™ Was Developed
            </p>

            <h2
              id="prime360-why-title"
              className="prime360-section-title"
            >
              Move from Fragmented Activity to
              <span> Connected Governance</span>
            </h2>
          </div>

          <div className="prime360-why-copy">
            <p>
              Organizations may have policies, audits, programs, technology,
              and data while still experiencing fragmented accountability,
              weak integration with business decisions, isolated psychological
              health efforts, and measures that reward activity rather than
              impact.
            </p>

            <p>
              PRIME360™ is intended to create a more connected pathway from
              governance intent to operational execution and evidence-informed
              improvement.
            </p>
          </div>

          <div className="prime360-fragmentation">
            <article>
              <span>01</span>
              <h3>Fragmented Accountability</h3>
              <p>
                Responsibility exists across the organization without a clear,
                connected governance structure.
              </p>
            </article>

            <article>
              <span>02</span>
              <h3>Disconnected Decisions</h3>
              <p>
                Safety information may not be integrated into enterprise,
                operational, or strategic decisions.
              </p>
            </article>

            <article>
              <span>03</span>
              <h3>Isolated Health Strategies</h3>
              <p>
                Occupational and psychological health efforts may operate as
                separate programs.
              </p>
            </article>

            <article>
              <span>04</span>
              <h3>Activity-Based Measures</h3>
              <p>
                Organizations may count actions without understanding adoption,
                effectiveness, or impact.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* STRATEGIC VALUE */}
      <section
        className="prime360-value"
        aria-labelledby="prime360-value-title"
      >
        <div className="prime360-grid-background" aria-hidden="true"></div>

        <div className="prime360-value-container">
          <header className="prime360-centered-heading">
            <p className="prime360-section-kicker">
              Strategic Value
            </p>

            <h2
              id="prime360-value-title"
              className="prime360-section-title"
            >
              A Common Structure for
              <span> Organizational Performance</span>
            </h2>

            <p>
              PRIME360™ is being developed to help organizations connect
              accountability, prevention, enterprise priorities, evidence, and
              continual improvement.
            </p>
          </header>

          <div className="prime360-value-grid">
            {strategicValues.map((value) => (
              <article
                className="prime360-value-card"
                key={value.title}
              >
                <span className="prime360-value-number">
                  {value.number}
                </span>

                <h3>{value.title}</h3>
                <p>{value.description}</p>

                <span
                  className="prime360-value-line"
                  aria-hidden="true"
                ></span>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FIVE CONNECTED ELEMENTS */}
      <section
        className="prime360-elements"
        aria-labelledby="prime360-elements-title"
      >
        <div className="prime360-elements-container">
          <header className="prime360-centered-heading">
            <p className="prime360-section-kicker">
              Five Connected Elements
            </p>

            <h2
              id="prime360-elements-title"
              className="prime360-section-title"
            >
              From Governance Intent to
              <span> System Renewal</span>
            </h2>

            <p>
              The public overview describes five connected elements without
              publishing the complete proprietary model, detailed assessment
              requirements, or scoring structure.
            </p>
          </header>

          <div className="prime360-element-list">
            <span
              className="prime360-element-track"
              aria-hidden="true"
            ></span>

            {connectedElements.map((element) => (
              <article
                className="prime360-element"
                key={element.title}
              >
                <div className="prime360-element-marker">
                  <span>{element.number}</span>
                </div>

                <div className="prime360-element-card">
                  <div className="prime360-element-heading">
                    <div>
                      <p>Connected Element {element.number}</p>
                      <h3>{element.title}</h3>
                    </div>

                    <span
                      className="prime360-element-index"
                      aria-hidden="true"
                    >
                      {element.number}
                    </span>
                  </div>

                  <div className="prime360-element-body">
                    <p className="prime360-element-description">
                      {element.description}
                    </p>

                    <ul className="prime360-element-areas">
                      {element.areas.map((area) => (
                        <li key={area}>
                          <span aria-hidden="true"></span>
                          {area}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <span
                    className="prime360-element-bottom-line"
                    aria-hidden="true"
                  ></span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* PRIME LENS */}
      <section
        className="prime360-lens"
        aria-labelledby="prime360-lens-title"
      >
        <div className="prime360-lens-background" aria-hidden="true">
          <span className="prime360-lens-glow"></span>
          <span className="prime360-lens-orbit"></span>
        </div>

        <div className="prime360-lens-container">
          <header className="prime360-centered-heading">
            <p className="prime360-section-kicker">
              The PRIME Lens
            </p>

            <h2
              id="prime360-lens-title"
              className="prime360-section-title"
            >
              Five Perspectives for
              <span> Connected Decision-Making</span>
            </h2>
          </header>

          <div className="prime360-lens-layout">
            <div className="prime360-lens-core" aria-hidden="true">
              <span>PRIME</span>
              <strong>360™</strong>
              <small>In Validation</small>
            </div>

            <div className="prime360-lens-grid">
              {primeLens.map((item) => (
                <article
                  className="prime360-lens-card"
                  key={item.title}
                >
                  <span className="prime360-lens-letter">
                    {item.letter}
                  </span>

                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STANDARDS CONTEXT */}
      <section
        className="prime360-standards"
        aria-labelledby="prime360-standards-title"
      >
        <div className="prime360-standards-container">
          <div className="prime360-standards-content">
            <p className="prime360-section-kicker">
              Standards Context
            </p>

            <h2
              id="prime360-standards-title"
              className="prime360-section-title"
            >
              Informed by Recognized
              <span> Safety and Governance References</span>
            </h2>

            <p className="prime360-standards-description">
              PRIME360™ is informed by recognized occupational health and
              safety, psychological health, governance, risk, and prevention
              references. These sources provide context for the developing
              framework but remain independent standards and programs.
            </p>

            <div className="prime360-standard-tags">
              {standardsContext.map((standard) => (
                <span key={standard}>{standard}</span>
              ))}
            </div>
          </div>

          <aside className="prime360-disclaimer">
            <div className="prime360-disclaimer-icon" aria-hidden="true">
              !
            </div>

            <div>
              <p className="prime360-disclaimer-label">
                Important Framework Notice
              </p>

              <h3>PRIME360™ remains in validation.</h3>

              <p>
                It is not a published external standard, certification scheme,
                legal requirement, regulator-authorized framework, or
                substitute for ISO 45001, CSA Z1003, COR, applicable
                legislation, or other recognized requirements.
              </p>

              <p>
                The complete proprietary model, detailed requirements,
                assessor evidence lists, scoring matrix, and full assessment
                tool are not included in this public overview.
              </p>
            </div>
          </aside>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        className="prime360-final"
        aria-labelledby="prime360-final-title"
      >
        <div className="prime360-final-background" aria-hidden="true">
          <span className="prime360-final-glow"></span>
          <span className="prime360-final-orbit"></span>
        </div>

        <div className="prime360-final-container">
          <div className="prime360-status">
            <span className="prime360-status-dot" aria-hidden="true"></span>
            <span>Developing Framework — In Validation</span>
          </div>

          <p className="prime360-section-kicker">
            Explore the Governance Approach
          </p>

          <h2
            id="prime360-final-title"
            className="prime360-final-title"
          >
            Strengthen the Connections Across
            <span> Your Safety System.</span>
          </h2>

          <p className="prime360-final-description">
            Discuss your organization’s governance priorities, system
            challenges, or interest in receiving an authorized executive
            overview of the developing PRIME360™ framework.
          </p>

          <div className="prime360-hero-buttons">
            <NavLink
              to="/contact"
              className="prime360-primary-button"
            >
              <span>Discuss PRIME360™</span>

              <span
                className="prime360-button-arrow"
                aria-hidden="true"
              >
                ↗
              </span>
            </NavLink>

            

            {/* <NavLink
              to="/contact"
              className="prime360-secondary-button"
            >
              <span>Request an Executive Brief</span>
              <span aria-hidden="true">→</span>
            </NavLink> */}

          <div className="partnership-pdf-link">
            <a
              href="/assets/PRIME360Form.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="partnership-pdf-label">
                View an Executive Brief of PRIME 360
              </span>

              <span
                className="partnership-pdf-arrow"
                aria-hidden="true"
              >
                ↗
              </span>
            </a>
          </div>
          </div>
        </div>
      </section>
    </main>
  );
}